/**
 * ScenarioMain.jsx
 * This is the main container for Scenario management UI.
 * - Uses Redux state for all scenario data (list and selected item)
 * - Uses the useScenariossData hook for all fetch/select logic
 * - Handles list view, selection, and details panel for a scenario
 */

import React, { useState, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useUpdateEffect } from "react-use";
import { useLocation, useNavigate } from "react-router-dom";

// Redux slices and actions
import { setSelectedScenario, clearSelectedScenario } from "src/slices/scenarioSlice";

// Custom hooks
import { useLookupsData } from "src/context/LookupsContext";
import { useScenariosData } from "src/hooks/useScenariosData";
import { useScenarioActions } from "src/hooks/useScenarioActions";
import { useDialogBox } from "src/hooks/useDialogBox";
import { useStepSaveGuard } from "src/hooks/useStepSaveGuard";

// Layout/UI
import { Box, Button, MetreaTooltip } from "@metrea-ui/v2";
import Stepper from "src/components/UI/common/Stepper";
import RecordNav from "src/components/UI/RecordNav/RecordNav";
import { PageHeader } from "../SiteLayout/Header";
import { PageFooter } from "../SiteLayout/Footer";
import { formFieldWidth } from "../Scenario/ScenarioVariables";
import ScenarioList from "./ScenarioList";
import ScenarioBasicInfo from "./ScenarioBasicInfo";
import ScenarioPreference from "./ScenarioPreference";
import ScenarioSelections from "./ScenarioSelections";
import ScenarioAccess from "./ScenarioConfigs";
import { InputFileUpload } from "src/components/UI/Upload/InputFileUpload";
import { getScenarioImportPayloadErrorMessages } from "./scenarioImportValidation";

// Utilities
import { isTestEnv } from "src/utils/appUtils";
import { scenarioAccess, getBudgetUpdate } from "src/utils/scenarioUtils";
import {
	Session,
	isDevTeam,
	isAdmin,
	isDesigner,
	isDeveloper,
	isOwner,
	isUserLogin,
} from "src/utils/sessionUtils";
import { findObjectByProperty, getIdFromPath, deepClone } from "src/utils/dataUtils";
import { debugLog, showTesting } from "src/utils/debugUtils";

// Constants
import { initialScenarioState } from "src/constants/initialStates";

const newScenarioInstruction =
	"Select a baseline for your scenario. The budget calculations will be generated based on the chosen baseline.";
const oldScenarioInstruction =
	"The Baseline Details below provide comprehensive information, including budget calculation values, available options, and other relevant data for the baseline on which the current scenario is based.";
const stepInstuctions = ["Input the basic information for this scenario.", "", "", "", ""];
const dataChangedAlert = "Data is modified. Please save (or revert) data before continue to another step.";

// --- Main Component ---
const ScenarioMain = (props) => {
	const renderCount = useRef(0);
	renderCount.current++;

	useEffect(() => {
		debugLog(2, "[ScenarioMain][FLOW] mounted", {});
	}, []);

	const dispatch = useDispatch();
	const location = useLocation();
	const navigate = useNavigate();

	// --- Redux Selectors ---
	const selectedScenario = useSelector((state) => state.scenarios.selectedScenario);
	const showList = useSelector((state) => !state.scenarios.selectedScenario);
	// Keep these selectors defensive so partial preloaded test state does not
	// crash the page before the shared error contract can run.
	const retrieveScenariosError = useSelector((state) => state.scenarios.error?.retrieve ?? null);
	const retrieveScenarioDetailError = useSelector((state) => state.scenarios.error?.retrieveOne ?? null);

	// --- Local State ---
	const initialSteps = ["Basic Information", "Strategic Preferences", "Selections", "Access Management"];

	const [pageMode, setPageMode] = useState(props.mode || "edit");
	const [steps, setSteps] = useState(initialSteps);
	const [activeStep, setActiveStep] = useState(0);
	const [completed, setCompleted] = useState({});
	const [errorStep, setErrorStep] = useState({});
	const [disableStep, setDisableStep] = useState({});
	const [errorStepTooltip, setErrorStepTooltip] = useState({});
	const [localScenario, setLocalScenario] = useState(deepClone(selectedScenario));
	const [dataChanged, setDataChanged] = useState(false);
	const [errorFound, setErrorFound] = useState(false);
	const [revertKey, setRevertKey] = useState(0);

	// --- Custom hooks ---
	const { lookups, allUserGroups } = useLookupsData();

	const { showDialog, withConfirmation, withError } = useDialogBox();

	const { fetchScenarios, fetchScenarioByIdOrUuid, handleSelectScenario } = useScenariosData();

	const {
		createScenarioAction,
		updateScenarioAction,
		deleteScenarioAction,
		copyScenarioAction,
		updateStatusAction,
		exportScenarioAction,
		importScenarioAction,
		dumpScenarioAction,
	} = useScenarioActions({
		fetchScenarioByIdOrUuid,
		fetchScenarios,
		clearDataError,
		handleBackToList,
		localScenario,
		activeStep,
		selectedScenario,
		lookups,
		setLocalScenario,
	});

	const isStep1Valid = useMemo(() => {
		if (!localScenario) return false;

		return localScenario.name?.trim() !== "" && !!localScenario.baseline_id && !errorFound;
	}, [localScenario, errorFound]);

	const stepValidators = {
		0: () => isStep1Valid,
		// Future:
		// 1: () => isStep2Valid,
		// 2: () => isStep3Valid,
	};

	const { activeStepIndex, canSave, hasSaveError } = useStepSaveGuard({
		activeStep,
		stepValidators,
		defaultHasError: errorFound,
	});

	// --- Derived Variables ---
	// Page Access Control
	const pageAccess = useMemo(() => {
		const data = {
			canEdit:
				pageMode === "edit" &&
				localScenario &&
				(scenarioAccess(localScenario, allUserGroups, Session().currUser) === 2 ||
					isDeveloper() ||
					isAdmin() ||
					isOwner(localScenario, Session().currUser.id)),
			canView: localScenario && scenarioAccess(localScenario, allUserGroups, Session().currUser) === 1,
			canAccess: localScenario && scenarioAccess(localScenario, allUserGroups, Session().currUser) >= 1,
		};
		return data;
	}, [localScenario, allUserGroups, pageMode]);

	// --- Effect: initial data load, user groups, deep links, etc. ---
	useEffect(() => {
		if (!isTestEnv && selectedScenario) {
			dispatch(clearSelectedScenario());
		}

		// Used for deep linking to a scenario by id/uuid in the URL
		const idFromPath = getIdFromPath(location.pathname);
		if (idFromPath) {
			fetchScenarioByIdOrUuid(idFromPath);
		} else fetchScenarios();

		debugLog(2, "[ScenarioMain][FLOW] initial data load", {
			selectedScenario: selectedScenario,
			idFromPath: idFromPath,
			propsId: props.id,
		});
	}, [location.pathname]);

	useEffect(() => {
		if (retrieveScenariosError) {
			// Scenario list-load failures should use the same app-wide dialog
			// contract as detail loads instead of staying buried in hook logs.
			withError(retrieveScenariosError, () => fetchScenarios(), {
				fallbackMessage: "Retrieve Scenarios Error",
			});
		}
	}, [fetchScenarios, retrieveScenariosError, withError]);

	useEffect(() => {
		if (!retrieveScenarioDetailError) return;

		const idFromPath = getIdFromPath(location.pathname);
		if (!idFromPath) return;

		const fallbackRoute = location.pathname.includes("/scenario/edit/") ? "/scenario/edit" : "/scenario";
		// Only show detail-load errors while a detail route is active; once we
		// navigate back to the list, leave the stale retrieveOne error alone.
		withError(retrieveScenarioDetailError, () => fetchScenarioByIdOrUuid(idFromPath), {
			fallbackMessage: "Retrieve Scenario Error",
			onClose: () => {
				navigate(fallbackRoute, { replace: true });
			},
		});
	}, [fetchScenarioByIdOrUuid, location.pathname, navigate, retrieveScenarioDetailError, withError]);

	useEffect(() => {
		const idFromPath = getIdFromPath(location.pathname);
		if (!idFromPath || !selectedScenario?.id) return;
		if (!Number.isNaN(Number(idFromPath))) return;

		const normalizedPath = location.pathname.includes("/scenario/edit/")
			? `/scenario/edit/${selectedScenario.id}`
			: `/scenario/${selectedScenario.id}`;

		if (location.pathname !== normalizedPath) {
			// Replace UUID deep links with the standard numeric route after the detail load succeeds.
			navigate(normalizedPath, { replace: true });
		}
	}, [location.pathname, navigate, selectedScenario?.id]);

	// --- Effect: sync local baseline on selection change ---
	useEffect(() => {
		const hasData = selectedScenario && Object.keys(selectedScenario || {}).length > 0;
		setLocalScenario(hasData ? deepClone(selectedScenario) : null);

		debugLog(3, "[ScenarioMain][STATE] selectedScenario changed", {
			selectedScenario: selectedScenario,
		});
	}, [selectedScenario]);

	// --- Effect: Update step status and access as localBaseline changes ---
	useUpdateEffect(() => {
		setErrorStep({});
		setDisableStep({});
		setErrorStepTooltip({});

		// Update Step Status
		if (localScenario) {
			if (isNew()) {
				setErrorStep({
					1: "Save step #1 to continue",
					2: "Save step #1 to continue",
					3: "Save step #1 to continue",
				});
				setDisableStep({
					1: "Not available",
					2: "Not available",
					3: "Not available",
				});
				setErrorStepTooltip({
					0: "Please save Step #1 before proceed to other steps.",
				});
			}

			if (pageAccess.canEdit) {
				setSteps(initialSteps);
			} else {
				setSteps(initialSteps.slice(0, -1));
			}

			// CHECK to verify if it's needed 2/9/2026
			// Reload Scenario if there's new item without scenario_item_configs (ex. new active items)
			if (localScenario.baseline_items) {
				localScenario.baseline_items.map((x) => {
					// debugLog(10, "[ScenarioMain] useUpdateEffect localScenario/completed", { item: x });

					const itemConfigs = Array.isArray(x?.scenario_item_configs)
						? x.scenario_item_configs
						: [];
					if (itemConfigs.length === 0) {
						// debugLog(10, "[ScenarioMain] useUpdateEffect localScenario/completed - page reloaded");
						// initFetchOne(localScenario.id);
					}
					return false;
				});
			}
		}

		debugLog(3, "[ScenarioMain][STATE] localScenario/completed updated", {
			localScenario: localScenario,
			completed: completed,
		});
	}, [localScenario, completed]);

	// --- Utility Functions ---
	const isNew = () => !localScenario?.id; // Prevent running logic if localScenario is null/undefined

	function clearDataError() {
		debugLog(3, "[ScenarioMain][STATE] clearDataError", {});
		setDataChanged(false);
		setErrorStepTooltip({});
		setErrorStep({});
		setDisableStep({});
	}

	// --- Handlers ---
	function handleBackToList() {
		debugLog(3, "[ScenarioMain][ACTION] handleBackToList", {});

		dispatch(clearSelectedScenario());
		setLocalScenario(null); // reset local state

		debugLog(2, "[ScenarioMain][FLOW] fetchScenarios started", {});
		fetchScenarios();

		handleReset();
		clearDataError();
	}

	const handleReset = () => {
		debugLog(3, "[ScenarioMain][ACTION] handleReset", {});
		setActiveStep(0);
		setCompleted({});
	};

	// const handleStep = (step) => () => {
	// 	debugLog(3, "[ScenarioMain] handleStep called", {
	// 		step: step,
	// 		activeStep: activeStep,
	// 		dataChanged: dataChanged,
	// 	});
	// 	setActiveStep(step);
	// };

	// Verify/save data here; Save Button action
	const handleComplete = () => {
		/**
		 * New validation logic using save guard
		 * - relies on canSave and hasSaveError from the guard to determine if data is valid for saving and to show error state in the UI. -ylee
		 *
		 * Note: canSave and hasSaveError are derived from the useStepSaveGuard hook,
		 * which centralizes the validation logic based on the active step and provided validators.
		 * This allows for more scalable and maintainable validation as the component grows in complexity. -ylee
		 *
		 * Don't really need it if RecordNav buttons are disabled when data is invalid,
		 * but keeping it here for extra safety and to show error dialog if somehow triggered with invalid data. -ylee
		 *
		 */
		if (!canSave) {
			showDialog({
				title: "Error",
				message: ["Data is incomplete.", " ", "Please make verify/validate all data to continue."],
				type: "error",
			});
			debugLog(3, "[ScenarioMain][ACTION] handleComplete.dataIncomplete", { activeStep: activeStep });
			return;
		}

		// Here is where to disable the "Completion Check" ylee 8/3/2023
		setCompleted((prev) => ({ ...prev, [activeStep]: true }));

		debugLog(3, "[ScenarioMain][ACTION] handleComplete", {
			activeStep: activeStep,
			newCompleted: { ...completed, [activeStep]: true },
			isNew: isNew(),
		});

		if (activeStep === 0 || activeStep === 1 || activeStep === 2 || activeStep === 3) {
			if (isNew()) {
				handleCreateScenario();
			} else {
				handleSaveScenario(activeStep);
			}
		}
	};

	const handleClearComplete = () => {
		setCompleted((prev) => {
			const next = { ...prev };
			delete next[activeStep];
			return next;
		});

		debugLog(3, "[ScenarioMain][ACTION] handleClearComplete", {
			newCompleted: (() => {
				const nextCompleted = { ...completed };
				delete nextCompleted[activeStep];
				return nextCompleted;
			})(),
			activeStep: activeStep,
		});
	};

	const handleLocalScenarioChange = (updateData) => {
		setLocalScenario((prev) => ({
			...prev,
			...updateData,
		}));

		setDataChanged(true);

		debugLog(3, "[ScenarioMain][ACTION] handleLocalScenarioChange", {
			updateData: updateData,
			activeStep: activeStep,
		});
	};

	const handleValidateData = (flag) => {
		setErrorFound(flag);
		debugLog(3, "[ScenarioMain][ACTION] handleValidateData", { errorFound: flag });
	};

	const handleStepChange = (nextStep) => {
		debugLog(3, "[ScenarioMain][ACTION] handleStepChange", { nextStep: nextStep });
		if (typeof nextStep === "number" && Number.isFinite(nextStep)) {
			setActiveStep(nextStep);
			return;
		}
		if (typeof nextStep === "string" && nextStep.trim() !== "" && !Number.isNaN(Number(nextStep))) {
			setActiveStep(Number(nextStep));
			return;
		}
		if (nextStep && typeof nextStep === "object") {
			const candidate = nextStep.index ?? nextStep.step;
			if (typeof candidate === "number" && Number.isFinite(candidate)) {
				setActiveStep(candidate);
				return;
			}
			if (
				typeof candidate === "string" &&
				candidate.trim() !== "" &&
				!Number.isNaN(Number(candidate))
			) {
				setActiveStep(Number(candidate));
			}
		}
	};

	const handleCompletedChange = (nextCompleted) => {
		debugLog(3, "[ScenarioMain][ACTION] handleCompletedChange", { nextCompleted: nextCompleted });
		setCompleted(nextCompleted);
	};

	const handleSaveScenario = async (step = 999, callback = null) => {
		// Add validation if needed, or call action directly
		debugLog(2, "[ScenarioMain][FLOW] updateScenarioAction started", { step: step });
		updateScenarioAction(step);
	};

	const handleCreateScenario = () => {
		debugLog(3, "[ScenarioMain][ACTION] handleCreateScenario", {});
		debugLog(2, "[ScenarioMain][FLOW] createScenarioAction started", {});
		createScenarioAction();
	};

	const handleUpdateStatus = (newStatus) => {
		// event.preventDefault();

		let arrayMsg = [
			`You are about to archive this Scenario, ${localScenario.name}.`,
			"This scenario would be removed from for the general users.",
			" ",
			"Do you want to proceed?",
		];
		if (newStatus === -1) {
			arrayMsg = [
				`You are about to permanently DELETE this Scenario, ${localScenario.name}.`,
				"The data will be removed from the application.",
				"This action CANNOT be undone.",
				" ",
				"Do you want to proceed?",
			];
		}

		if (newStatus !== 1) {
			withConfirmation(
				{
					title: "Warning",
					message: arrayMsg,
				},
				() => updateStatusAction(localScenario.id, newStatus),
			);
		} else {
			updateStatusAction(localScenario.id, newStatus);
		}

		debugLog(3, "[ScenarioMain][ACTION] handleUpdateStatus", { newStatus: newStatus });
	};

	const handleChangeStatusFromList = async (id, newStatus) => {
		updateStatusAction(id, newStatus, false); // false: don't show confirmation MsgBox

		// handleBackToList();  // already called in updateStatusAction()
		debugLog(3, "[ScenarioMain][ACTION] handleChangeStatusFromList", { id: id, newStatus: newStatus });
	};

	const handleCopyBaseline = () => {
		withConfirmation(
			{
				title: "Warning",
				message: [
					`You are about to make a copy of this scenario, ${localScenario.name}.`,
					" ",
					"Do you want to proceed?",
				],
			},
			() => copyScenarioAction(localScenario.id),
		);

		debugLog(3, "[ScenarioMain][ACTION] handleCopyBaseline", { localScenarioId: localScenario.id });
	};

	const handleDeleteBaseline = () => {
		withConfirmation(
			{
				title: "Warning",
				message: [
					`You are about to permanently delete/purge this scenario, ${localScenario.name}.`,
					"The data will be removed from the database.",
					"This action CANNOT be undone.",
					" ",
					"Do you want to proceed?",
				],
			},
			() => deleteScenarioAction(localScenario.id),
		);

		debugLog(3, "[ScenarioMain][ACTION] handleDeleteBaseline", { localScenarioId: localScenario.id });
	};

	const handleExportScenario = async () => {
		withConfirmation(
			{
				title: "Confirm",
				message: [
					`You are about to export this scenario, ${localScenario.name}, in the JSON format..`,
					" ",
					"Do you want to proceed?",
				],
				checkboxLabel: showTesting() ? "Include photos (images & thumbnails)" : null,
				checkboxDefault: false,
			},
			(checked) => exportScenarioAction({ includePhotos: !!checked }),
		);

		debugLog(3, "[ScenarioMain][ACTION] handleExportScenario", { localScenarioId: localScenario.id });
	};

	const handleDataDump = async () => {
		withConfirmation(
			{
				title: "Confirm",
				message: [
					`You are about to dump data of this scenario, ${localScenario.name}, in the XLSX format.`,
					" ",
					"Do you want to proceed?",
				],
			},
			() => dumpScenarioAction(),
		);

		debugLog(3, "[ScenarioMain][ACTION] handleDataDump", { localScenarioId: localScenario.id });
	};

	const handleImportScenario = (data) => {
		debugLog(3, "[ScenarioMain][ACTION] handleImportScenario", { importedData: data });
		withConfirmation(
			{
				title: "Confirm",
				message: [
					`You are about to import this scenario, ${data.name}.`,
					" ",
					"Do you want to proceed?",
				],
			},
			() => {
				// Keep the client-side import guard aligned with the backend shape
				// validator so obvious cross-module files show the specific Scenario
				// import message before any mutation request is sent.
				const importPayloadErrorMessages = getScenarioImportPayloadErrorMessages(data);
				if (importPayloadErrorMessages) {
					// Defer the follow-up error dialog until after the confirmation
					// modal closes; otherwise the confirmation callback's cleanup will
					// immediately clear the new error modal before it becomes visible.
					setTimeout(() => {
						showDialog({
							title: "Error",
							message: importPayloadErrorMessages,
							type: "error",
						});
					}, 0);
					return;
				}

				const now = new Date();
				const importData = {
					...data,
					name: `${data.name} (imported - ${data.importDt})`,
					description: `${data.description ?? ""}\n\nImport file: ${data.filename}`,
					from_id: -data.id,
					id: null,
					createdBy: Session().currUser.id,
					createdAt: now,
					updatedBy: Session().currUser.id,
					updatedAt: now,
					baseline: {
						...data.baseline,
						name: `${data.baseline.name} (imported: ${now.toLocaleDateString("en-US")})`,
						description:
							`${data.baseline.description ?? ""}\n\nImport file: ${data.filename}` +
							`\nImport on: ${data.importDt}`,
						from_id: -data.id,
						id: null,
						createdBy: Session().currUser.id,
						createdAt: now,
						updatedBy: Session().currUser.id,
						updatedAt: now,
					},
				};

				importScenarioAction(importData);
			},
		);
	};

	const handleRevertData = () => {
		debugLog(3, "[ScenarioMain][ACTION] handleRevertData", { localScenarioId: localScenario.id });
		debugLog(2, "[ScenarioMain][FLOW] fetchScenarioByIdOrUuid started", { id: localScenario?.id });
		fetchScenarioByIdOrUuid(localScenario.id);
		setCompleted({});
		clearDataError();
		setRevertKey((k) => k + 1);
	};

	const handleSelectionChange = ({ option, item: updatedItem }) => {
		debugLog(3, "[ScenarioMain][ACTION] handleSelectionChange", {
			optionId: option?.id ?? null,
			value: option?.value,
			updatedItemId: updatedItem?.id ?? null,
		});
		const { id, value } = option;
		let target = null;
		try {
			target = JSON.parse(id);
		} catch (error) {
			debugLog(2, "[ScenarioMain][ERROR] handleSelectionChange.invalidOptionId", { id, error });
			showDialog({
				title: "Error",
				message: ["Invalid selection payload. Please try again."],
				type: "error",
			});
			return;
		}

		let newScenario = deepClone(localScenario);
		const baselineItems = Array.isArray(newScenario?.baseline_items) ? newScenario.baseline_items : [];
		let scenarioItemConfigs = Array.isArray(newScenario?.scenario_item_configs)
			? [...newScenario.scenario_item_configs]
			: [];

		let counter = 0;
		// Ensure all scenario_item_configs from baseline_items are present in scenario_item_configs
		baselineItems.forEach((item) => {
			const itemConfigs = Array.isArray(item?.scenario_item_configs) ? item.scenario_item_configs : [];
			itemConfigs.forEach((cfg) => {
				if (!findObjectByProperty(scenarioItemConfigs, "id", cfg.id)) {
					counter++;
					scenarioItemConfigs.push(cfg);
				}
			});
		});

		// Update scenario_item_configs array
		newScenario.scenario_item_configs = scenarioItemConfigs.map((cfg) =>
			cfg.id === target.id ? { ...cfg, update_flag: target.update_flag, comp_value1: value } : cfg,
		);

		// Update scenario_item_configs in each baseline_item
		newScenario.baseline_items = baselineItems.map((bi) => {
			const itemConfigs = Array.isArray(bi?.scenario_item_configs) ? bi.scenario_item_configs : [];
			if (itemConfigs.find((cfg) => cfg.id === target.id)) {
				const updatedConfigs = itemConfigs.map((cfg) =>
					cfg.id === target.id
						? { ...cfg, update_flag: target.update_flag, comp_value1: value }
						: cfg,
				);
				return { ...bi, scenario_item_configs: updatedConfigs };
			}
			return bi;
		});

		// CRITICAL: Replace the updated item in baseline_items by id (no merge)
		if (updatedItem?.id != null) {
			newScenario.baseline_items = newScenario.baseline_items.map((bi) =>
				bi.id === updatedItem.id ? updatedItem : bi,
			);
		}

		// Optionally:  Recompute budget/quantity, etc.
		const updatedScenario =
			newScenario?.baseline && Array.isArray(newScenario?.baseline_items)
				? getBudgetUpdate(newScenario)
				: newScenario;

		setLocalScenario(deepClone(updatedScenario)); // This triggers UI update

		setDataChanged(true);
		handleClearComplete();

		debugLog(3, "[ScenarioMain][ACTION] handleSelectionChange", {
			target: target,
			option: option,
			updatedItem: updatedItem,
			counter: counter,
			id_value: `${id}_${value}`,
			newScenario: updatedScenario,
		});
	};

	const handleSelectionError = (error) => {
		let arrayMsg = [];
		if (error === "overMax") {
			arrayMsg = [
				"The number you entered is too big.",
				" ",
				"The total retirement number can't be greater than the total inventory.",
			];
		} else if (error === "overMin") {
			arrayMsg = [
				"The number you entered is too small.",
				" ",
				"The minimum number is the negative value of the planned value.",
			];
		} else if (error === "notNumber") {
			arrayMsg = ["Please enter a valid number."];
		}

		showDialog({
			title: "Error",
			message: arrayMsg,
			type: "error",
		});

		debugLog(3, "[ScenarioMain][ACTION] handleSelectionError", { error: error });
	};

	const clickNewHandler = () => {
		debugLog(3, "[ScenarioMain][ACTION] clickNewHandler", {});
		dispatch(setSelectedScenario(initialScenarioState));
	};

	const clickBackToListHandler = (option) => {
		debugLog(3, "[ScenarioMain][ACTION] clickBackToListHandler", { option: option });
		handleBackToList();
	};

	const clickSaveHandler = (option) => {
		debugLog(3, "[ScenarioMain][ACTION] clickSaveHandler", { option: option });
		handleComplete();
	};

	const clickArchiveHandler = (option, newStatus) => {
		debugLog(3, "[ScenarioMain][ACTION] clickArchiveHandler", { option: option, newStatus: newStatus });
		handleUpdateStatus(newStatus);
	};

	const clickRevertHandler = (option) => {
		debugLog(3, "[ScenarioMain][ACTION] clickRevertHandler", { option: option });
		handleRevertData();
	};

	const clickPurgeHandler = (option) => {
		debugLog(3, "[ScenarioMain][ACTION] clickPurgeHandler", { option: option });
		handleDeleteBaseline();
	};

	const clickDeleteHandler = (option) => {
		debugLog(3, "[ScenarioMain][ACTION] clickDeleteHandler", { option: option });
		handleUpdateStatus(-1);
	};

	const clickDataDumpHandler = (option) => {
		debugLog(3, "[ScenarioMain][ACTION] clickDataDumpHandler", { option: option });
		handleDataDump();
	};

	const clickExportHandler = (option) => {
		debugLog(3, "[ScenarioMain][ACTION] clickExportHandler", { option: option });
		handleExportScenario();
	};

	const clickCopyHandler = (option) => {
		debugLog(3, "[ScenarioMain][ACTION] clickCopyHandler", { option: option });
		handleCopyBaseline();
	};

	// --- Logging: Render and State Snapshots ---
	// debugLog(2, "\n↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓\n");
	debugLog(9, "[ScenarioMain][FLOW] render", {
		renderCount: renderCount.current,
		selectedScenario: selectedScenario,
	});
	debugLog(9, "[ScenarioMain][FLOW] renderState", {
		props: props,
		localScenario: localScenario,
		dataChanged: dataChanged,
		errorFound: errorFound,
	});
	// debugLog(4, "\n↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑\n");

	return (
		<div data-testid="ScenarioMain">
			<PageHeader pageTitle="Scenarios" actions={true}>
				{!localScenario && pageMode === "edit" && (isAdmin() || isDeveloper()) && (
					<InputFileUpload
						label="Import"
						color="secondary"
						toolTip="Import Scenario (from a previously exported JSON file)"
						onLoad={handleImportScenario}
					/>
				)}
				{/* ToDo: Any login users should allow to create a new scenario */}
				{!localScenario && isUserLogin() && (isAdmin() || isDeveloper() || isDesigner()) && (
					<MetreaTooltip content="Add New Scenario">
						<Button onClick={clickNewHandler} style={{ padding: "10px" }}>
							Add New
						</Button>
					</MetreaTooltip>
				)}
			</PageHeader>

			<Box className="pageContent" pt={0}>
				{/* List view */}
				{!localScenario && (
					<div className="col-md-12">
						{showList && (
							<>
								<ScenarioList
									mode={pageMode}
									formFieldWidth={formFieldWidth}
									onSelectScenario={handleSelectScenario}
									onChangeStatus={handleChangeStatusFromList}
								/>
							</>
						)}
					</div>
				)}

				{/* Detail view: Stepper and forms */}
				{localScenario && (
					<>
						<Box id="top" w="100%">
							<RecordNav
								callFrom="Scenario"
								showBack
								{...(pageMode === "edit"
									? {
											...(showTesting() && isDevTeam() ? { showPurge: true } : {}),
											...(isAdmin()
												? {
														showDelete: true,
														showDataDump: true,
														showExport: true,
													}
												: {}),
											...(pageAccess.canEdit
												? {
														showCopy: true,
														...(localScenario.status_id !== 1
															? { showRestore: true }
															: { showArchive: true }),
														showRevert: true,
														showSave: true,
													}
												: {}),
										}
									: {})}
								onClickBackToList={clickBackToListHandler}
								onClickPurge={clickPurgeHandler}
								onClickDelete={clickDeleteHandler}
								onClickDataDump={clickDataDumpHandler}
								onClickExport={clickExportHandler}
								onClickCopy={clickCopyHandler}
								onClickArchive={clickArchiveHandler}
								onClickRevert={clickRevertHandler}
								onClickSave={clickSaveHandler}
								hasError={hasSaveError}
								// Keep non-save actions gated while step 3 has unsaved changes.
								disabled={dataChanged && activeStepIndex === 2}
								// Explicitly allow Revert/Save in step 3 so users can resolve dirty state.
								disabledRevert={false}
								disabledSave={false}
							/>

							{/* ToDo: Need to update Handlers. ex. setActiveStep, setCompleted */}
							<Stepper
								title="Scenario Steps"
								steps={steps}
								// control the active step
								value={activeStep}
								onChange={handleStepChange}
								// completion map
								completed={completed}
								onCompletedChange={handleCompletedChange}
								disabledSteps={disableStep} // map or array is fine
								errorStepTooltip={errorStepTooltip} // small helper icon with tooltip
								errorStep={errorStep} // fallback messages (optional, but supported)
								dataChanged={dataChanged && !isNew()} // red “changed” icon on the active step
								dataChangedTooltip={dataChangedAlert}
								blockNavigationOnChange // when dataChanged, other steps disabled
								isNew={isNew()} // only shows helper icon during “new”
								// Save logic; return false to block completion
								onSave={(idx) => {
									let ok = true;
									if (idx === 0 && (localScenario?.name?.trim?.() ?? "") === "") ok = false;
									return ok;
								}}
							/>
							<div>
								<React.Fragment>
									<Box mt="20px">
										{pageAccess.canEdit && (
											<Box className="mx-2 text-xs">{stepInstuctions[activeStep]}</Box>
										)}
										{activeStep === 0 && (
											<Box data-testid="tab-basic-information">
												{pageAccess.canEdit && (
													<Box className="mx-2 mb-8 text-xs">
														<br />
														{localScenario.id
															? `${oldScenarioInstruction}`
															: `${newScenarioInstruction}`}
													</Box>
												)}
												<ScenarioBasicInfo
													formFieldWidth={formFieldWidth}
													item={localScenario}
													mode={pageAccess?.canEdit ? pageMode : "view"}
													onLocalScenarioChange={handleLocalScenarioChange}
													onValidateData={handleValidateData}
													onClearComplete={handleClearComplete}
													onSetDataChanged={setDataChanged}
												/>
											</Box>
										)}
										{activeStep === 1 && (
											<Box data-testid="tab-strategic-preferences">
												<ScenarioPreference
													formFieldWidth={formFieldWidth}
													item={localScenario}
													mode={pageAccess?.canEdit ? pageMode : "view"}
													onSaveDataToDB={handleSaveScenario}
													onLocalScenarioChange={handleLocalScenarioChange}
													onClearComplete={handleClearComplete}
													onError={handleSelectionError}
												/>
											</Box>
										)}
										{activeStep === 2 && (
											<Box data-testid="tab-selections">
												<ScenarioSelections
													item={localScenario}
													revertKey={revertKey}
													mode={pageAccess?.canEdit ? pageMode : "view"}
													onChange={handleSelectionChange}
													onError={handleSelectionError}
													onSaveDataToDB={handleSaveScenario}
												/>
											</Box>
										)}
										{activeStep === 3 && pageAccess.canAccess && (
											<Box data-testid="tab-access-management">
												<ScenarioAccess
													formFieldWidth={formFieldWidth}
													item={localScenario}
													mode={pageAccess?.canEdit ? pageMode : "view"}
													groupId={200}
													onLocalScenarioChange={handleLocalScenarioChange}
													onClearComplete={handleClearComplete}
												/>
											</Box>
										)}
									</Box>
								</React.Fragment>
							</div>
						</Box>
					</>
				)}
				<PageFooter />
			</Box>
		</div>
	);
};

export default ScenarioMain;
